import type { WidgetTheme } from '../types';

const loadedFonts = new Set<string>(['Inter']);
const loadedUrls = new Set<string>();

function loadFontStylesheet(url: string): void {
  if (loadedUrls.has(url)) return;
  loadedUrls.add(url);

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

function loadGoogleFont(family: string): void {
  const normalized = family.trim();
  if (!normalized || loadedFonts.has(normalized)) return;
  loadedFonts.add(normalized);

  loadFontStylesheet(
    `https://fonts.googleapis.com/css2?family=${encodeURIComponent(normalized)}:wght@400;500;600;700;800&display=swap`,
  );
}

export function applyTheme(theme: WidgetTheme, forceDark?: boolean): void {
  const root = document.documentElement;
  root.style.setProperty('--mw-primary', theme.primary_color);
  root.style.setProperty('--mw-secondary', theme.secondary_color);
  root.style.setProperty('--mw-bg', theme.background_color);
  root.style.setProperty('--mw-text', theme.text_color);
  root.style.setProperty('--mw-font', theme.font_family);
  root.style.setProperty('--mw-radius', `${theme.border_radius}px`);

  if (theme.font_url) {
    loadFontStylesheet(theme.font_url);
  } else {
    loadGoogleFont(theme.font_family);
  }

  root.style.setProperty('--mw-btn-style', theme.button_style);

  const primaryLum = relativeLuminance(theme.primary_color);
  root.style.setProperty('--mw-primary-text', primaryLum > 0.5 ? '#1A1A2E' : '#FFFFFF');
  root.style.setProperty('--mw-text-muted', addAlpha(theme.text_color, 0.6));
  root.style.setProperty('--mw-border', addAlpha(theme.text_color, 0.12));
  root.style.setProperty('--mw-surface', addAlpha(theme.text_color, 0.04));

  const bgLum = relativeLuminance(theme.background_color);
  const isDark = forceDark ?? bgLum < 0.2;
  root.classList.toggle('mw-dark', isDark);
}

export function detectColorScheme(): 'dark' | 'light' | null {
  const url = new URL(window.location.href);
  const param = url.searchParams.get('theme');
  if (param === 'dark') return 'dark';
  if (param === 'light') return 'light';
  return null;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function addAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function formatCurrency(
  amountInCents: number,
  currency: string,
): string {
  const amount = amountInCents / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
}

/**
 * Formats an amount in **major** currency units (e.g. 12.5 → $12.50) with no /100.
 * Use for table pricing and cart subtotals expressed in dollars. Fee/tax/totals from
 * `POST /mixed/sales-calc` are in **cents** — use {@link formatCurrency} for those.
 */
export function formatCurrencyMajor(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
}

export function formatDate(isoDate: string, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: timezone,
    }).format(new Date(isoDate));
  } catch {
    return new Date(isoDate).toLocaleString();
  }
}
